import {
  getAgeFromBirthYear,
  getPriceBreakdown,
  getTariffLabel,
} from '@getpopsure/private-health-insurance-pricing-engine';

import { getPublicHealthContribution } from '../util';
import {
  CalculatorInputData,
  CalculatorOutputData,
  CHILD_AGE_THRESHOLD,
  ChildContentVariant,
  employmentStatusToHallescheThreshold,
  GENERAL_AGE_THRESHOLD,
  GENERAL_NON_EMPLOYEE_INCOME_THRESHOLD,
  GeneralContentVariant,
  HANSE_MERKUR_AGE_THRESHOLD,
  MainContentVariant,
  SpouseContentVariant,
  UnusedEmployerContributions,
  YEARLY_MINIJOB_THRESHOLD,
} from './model';

const isOlderThanThreshold = (threshold: number, birthYear?: number): boolean =>
  !!birthYear && birthYear > 999 && getAgeFromBirthYear(birthYear) > threshold;

export const getMainCalculatorOutputData = ({
  calculatorInputData,
  isSimpleMode,
}: {
  calculatorInputData?: CalculatorInputData;
  isSimpleMode?: boolean;
}): CalculatorOutputData<GeneralContentVariant | MainContentVariant> => {
  const {
    birthYear,
    employmentStatus,
    hasEUPassport,
    hasPermanentResidencyPermit,
    income,
    isPlanningToStayMoreThan5Years,
    plan,
  } = calculatorInputData || {};

  if (isOlderThanThreshold(GENERAL_AGE_THRESHOLD, birthYear)) {
    return { contentVariant: 'TOO_OLD' };
  }

  const hasAnsweredPrivateLightEligibilityQuestions = isSimpleMode
    ? true
    : hasEUPassport ||
      hasPermanentResidencyPermit ||
      isPlanningToStayMoreThan5Years !== undefined;

  const hasAnsweredAllQuestions =
    income !== undefined &&
    birthYear !== undefined &&
    birthYear > 999 &&
    employmentStatus !== undefined &&
    hasAnsweredPrivateLightEligibilityQuestions;

  if (!hasAnsweredAllQuestions) {
    return { contentVariant: 'NO_PRICE' };
  }

  const hallescheIncomeThreshold =
    employmentStatusToHallescheThreshold[employmentStatus];

  const incomeIsUnderHallescheThreshold = income < hallescheIncomeThreshold;

  if (incomeIsUnderHallescheThreshold) {
    const isOverHanseMerkurAgeThreshold =
      getAgeFromBirthYear(birthYear) > HANSE_MERKUR_AGE_THRESHOLD;

    switch (employmentStatus) {
      case 'EMPLOYED_IN_GERMANY':
        return { contentVariant: 'EMPLOYED_IN_GERMANY--INCOME_TOO_LOW' };

      case 'OTHER':
        if (
          !isOverHanseMerkurAgeThreshold &&
          income < GENERAL_NON_EMPLOYEE_INCOME_THRESHOLD
        ) {
          return { contentVariant: 'OTHER--INCOME_TOO_LOW' };
        }

        if (isOverHanseMerkurAgeThreshold) {
          return {
            contentVariant:
              'OTHER--INCOME_TOO_LOW_BUT_TOO_OLD_FOR_HANSE_MERKUR',
          };
        }

        return { contentVariant: 'OTHER--HANSE_MERKUR_REDIRECT' };

      case 'EMPLOYED_OUTSIDE_OF_GERMANY':
      case 'SELF_EMPLOYED':
      default:
        if (
          !isOverHanseMerkurAgeThreshold &&
          income < GENERAL_NON_EMPLOYEE_INCOME_THRESHOLD
        ) {
          return { contentVariant: 'SELF_EMPLOYED--INCOME_TOO_LOW' };
        }

        if (isOverHanseMerkurAgeThreshold) {
          return {
            contentVariant:
              'SELF_EMPLOYED--INCOME_TOO_LOW_BUT_TOO_OLD_FOR_HANSE_MERKUR',
          };
        }

        return { contentVariant: 'SELF_EMPLOYED--HANSE_MERKUR_REDIRECT' };
    }
  }

  const priceBreakdown = getPriceBreakdown({
    tariff: plan ?? 'NKSelectS',
    birthYear,
    income,
    employmentStatus,
    includeUnusedEmployerContributions: true,
  });

  const monthlyPrice = priceBreakdown.tariff.totalPrice;
  const { unusedEmployerContributions } = priceBreakdown.contributions;

  const publicPrice =
    getPublicHealthContribution({
      income,
      birthYear,
      employmentStatus,
    })?.total ?? undefined;

  return {
    priceData: {
      publicPrice,
      monthlyPrice,
      unusedEmployerContributions,
      ...(priceBreakdown.contributions.employer
        ? {
            discountedMonthlyPrice: priceBreakdown.contributions.own,
          }
        : {}),
      cashbackData: priceBreakdown.cashback,
      planStr: `${getTariffLabel(plan ?? 'NKSelectS')} plan`,
    },
  };
};

export const getSpouseCalculatorOutputData = ({
  calculatorInputData,
  unusedMainEmployerContributions,
  mainHasAnsweredAllQuestions,
  mainIsNotEligible,
  mainOnHanseMerkur,
  isSimpleMode,
}: {
  calculatorInputData?: CalculatorInputData;
  unusedMainEmployerContributions?: UnusedEmployerContributions;
  mainHasAnsweredAllQuestions: boolean;
  mainIsNotEligible: boolean;
  mainOnHanseMerkur: boolean;
  isSimpleMode?: boolean;
}): CalculatorOutputData<GeneralContentVariant | SpouseContentVariant> => {
  const {
    birthYear,
    employmentStatus,
    hasEUPassport,
    hasPermanentResidencyPermit,
    income,
    isPlanningToStayMoreThan5Years,
    plan,
  } = calculatorInputData || {};

  if (isOlderThanThreshold(GENERAL_AGE_THRESHOLD, birthYear)) {
    return { contentVariant: 'SPOUSE--TOO_OLD' };
  }

  if (!mainHasAnsweredAllQuestions) {
    return { contentVariant: 'SPOUSE--MAIN_NOT_ANSWERED' };
  }

  if (mainIsNotEligible) {
    return { contentVariant: 'SPOUSE--MAIN_NOT_ELIGIBLE' };
  }

  if (mainOnHanseMerkur) {
    return { contentVariant: 'SPOUSE--MAIN_ON_HANSE_MERKUR' };
  }

  const hasAnsweredPrivateLightEligibilityQuestions = isSimpleMode
    ? true
    : hasEUPassport ||
      hasPermanentResidencyPermit ||
      isPlanningToStayMoreThan5Years !== undefined;

  const hasAnsweredAllQuestions =
    income !== undefined &&
    birthYear !== undefined &&
    birthYear > 999 &&
    employmentStatus !== undefined &&
    hasAnsweredPrivateLightEligibilityQuestions;

  const isConsideredDependent = (income ?? 0) < YEARLY_MINIJOB_THRESHOLD;

  if (!hasAnsweredAllQuestions) {
    return { contentVariant: 'NO_PRICE' };
  }

  if (isConsideredDependent) {
    const priceBreakdown = getPriceBreakdown({
      tariff: plan ?? 'NKSelectS',
      birthYear,
      sickDayPayout: 0,
    });

    const monthlyPrice = priceBreakdown.contributions.own;
    const { longTermCare } = priceBreakdown.tariff;

    let discountedMonthlyPrice;
    let maxDiscount = 0;
    let maxLongTermCareDiscount = 0;

    if (unusedMainEmployerContributions) {
      const monthlyPriceWithoutLongTermCare = monthlyPrice - longTermCare;

      maxDiscount = Math.min(
        unusedMainEmployerContributions.base,
        monthlyPriceWithoutLongTermCare / 2
      );

      const discountedMonthlyPriceWithoutLongTermCare =
        monthlyPriceWithoutLongTermCare - maxDiscount;

      maxLongTermCareDiscount = Math.min(
        unusedMainEmployerContributions.longTermCare,
        longTermCare / 2
      );

      const discountedLongTermCare = longTermCare - maxLongTermCareDiscount;

      discountedMonthlyPrice =
        discountedMonthlyPriceWithoutLongTermCare + discountedLongTermCare;
    }

    return {
      priceData: {
        publicPrice: 0,
        monthlyPrice,
        discountedMonthlyPrice,
        isDependent: isConsideredDependent,
        ...(unusedMainEmployerContributions
          ? {
              unusedEmployerContributions: {
                base: unusedMainEmployerContributions.base - maxDiscount,
                longTermCare:
                  unusedMainEmployerContributions.longTermCare -
                  maxLongTermCareDiscount,
              },
            }
          : {}),
        cashbackData: priceBreakdown.cashback,
        planStr: `${getTariffLabel(plan ?? 'NKSelectS')} plan`,
      },
    };
  }

  const { priceData, contentVariant } = getMainCalculatorOutputData({
    calculatorInputData,
    isSimpleMode,
  });

  const spouseContentVariant:
    | GeneralContentVariant
    | SpouseContentVariant
    | undefined =
    contentVariant !== 'NO_PRICE'
      ? contentVariant
        ? `SPOUSE--${contentVariant}`
        : undefined
      : contentVariant;

  return priceData
    ? {
        priceData,
      }
    : {
        contentVariant: spouseContentVariant,
      };
};

export const getChildCalculatorOutputData = ({
  calculatorInputData,
  unusedParentEmployerContributions,
  mainHasAnsweredAllQuestions,
  mainIsNotEligible,
  mainOnHanseMerkur,
  isSimpleMode,
}: {
  calculatorInputData: CalculatorInputData;
  unusedParentEmployerContributions?: UnusedEmployerContributions;
  mainHasAnsweredAllQuestions: boolean;
  mainIsNotEligible: boolean;
  mainOnHanseMerkur: boolean;
  isSimpleMode?: boolean;
}): CalculatorOutputData<GeneralContentVariant | ChildContentVariant> => {
  const {
    birthYear,
    hasEUPassport,
    hasPermanentResidencyPermit,
    isPlanningToStayMoreThan5Years,
    plan,
  } = calculatorInputData;

  if (isOlderThanThreshold(CHILD_AGE_THRESHOLD, birthYear)) {
    return { contentVariant: 'CHILD--TOO_OLD' };
  }

  if (!mainHasAnsweredAllQuestions) {
    return { contentVariant: 'CHILD--MAIN_NOT_ANSWERED' };
  }

  if (mainIsNotEligible) {
    return { contentVariant: 'CHILD--MAIN_NOT_ELIGIBLE' };
  }

  if (mainOnHanseMerkur) {
    return { contentVariant: 'CHILD--MAIN_ON_HANSE_MERKUR' };
  }

  const hasAnsweredPrivateLightEligibilityQuestions = isSimpleMode
    ? true
    : hasEUPassport ||
      hasPermanentResidencyPermit ||
      isPlanningToStayMoreThan5Years !== undefined;

  const hasAnsweredChildQuestions =
    birthYear !== undefined &&
    birthYear > 999 &&
    hasAnsweredPrivateLightEligibilityQuestions;

  if (!hasAnsweredChildQuestions) {
    return { contentVariant: 'NO_PRICE' };
  }

  // Price without income protection and long term care
  const priceBreakdown = getPriceBreakdown({
    tariff: plan ?? 'NKSelectS',
    birthYear,
    sickDayPayout: 0,
    excludeLongTermCare: true,
  });

  const monthlyPrice = priceBreakdown.contributions.own;
  const discountedMonthlyPrice = unusedParentEmployerContributions
    ? Math.max(
        monthlyPrice - unusedParentEmployerContributions.base,
        monthlyPrice / 2
      )
    : undefined;

  return {
    priceData: {
      publicPrice: 0,
      monthlyPrice,
      discountedMonthlyPrice,
      isDependent: true,
      cashbackData: priceBreakdown.cashback,
      planStr: `${getTariffLabel(plan ?? 'NKSelectS')} plan`,
    },
  };
};
